"use client";

import { Button } from "@/components/ui/button";
import { signInWithGoogle } from "@/lib/auth/sign-in-with-google";
import { useState } from "react";
import { toast } from "sonner";

type ProviderAuthButtonProps = {
  disabled?: boolean;
  afterSignin?: () => void;
};

function handoffFromLocation() {
  if (typeof window === "undefined") {
    return undefined;
  }
  const params = new URLSearchParams(window.location.search);
  const handoffId = params.get("handoff_id");
  const token = params.get("token");
  const landingId = params.get("landing_id");
  if (!handoffId && !token && !landingId) {
    return undefined;
  }
  return { handoffId, token, landingId };
}

export function ProviderAuthButton({
  disabled = false,
}: ProviderAuthButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleProviderLogin = async () => {
    setIsLoading(true);
    try {
      const returnUrl = window.location.pathname + window.location.search;
      const { error, data } = await signInWithGoogle(
        returnUrl,
        handoffFromLocation(),
      );
      if (error) {
        toast.error(error.message);
        setIsLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to sign in with provider",
      );
    }
  };

  return (
    <>
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-zinc-200 dark:border-zinc-700" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-zinc-500 dark:bg-zinc-900 dark:text-zinc-400">
            or
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="h-11 w-full border-zinc-200 bg-zinc-200 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:bg-zinc-700"
        onClick={handleProviderLogin}
        disabled={disabled || isLoading}
      >
        <svg
          className="mr-2 h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {isLoading ? "Signing in..." : "Continue with Google"}
      </Button>
    </>
  );
}
